import {
  List, ListVote, ListUser, Comment,
} from 'lib/types';
import { useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import {
  Avatar, Tooltip, Badge, BadgeProps, Select, MenuItem, InputLabel, FormControl, Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ThumbUp, ThumbUpOffAlt, Chat, ChatBubble, OpenInNew,
} from '@mui/icons-material';
import { useUserLists } from 'lib/contexts/userListsContext';
import { formatLocalString } from 'lib/utils/datetime';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { MobileTooltip } from 'components/MobileTooltip';
import { CreatorsListUser } from 'lib/types/users';
import { ListCommentModal } from 'features/Creators/ListCommentModal';
import { CreatorStage } from 'lib/types/teams';
import { Modal } from 'components/Modal';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -4,
    top: 5,
    border: `1px solid ${theme.palette.background.paper}`,
    fontSize: '10px',
  },
}));

interface Props {
  list?: List;
  user: CreatorsListUser;
  setApplicationId?: (appId?: number) => void;
  children: ReactNode;
  creatorStages?: CreatorStage[];
  filteredStages: number[];
}

export const ListDetailsWrapper = ({
  list, user, setApplicationId, children, creatorStages, filteredStages,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const { lists, setLists } = useUserLists();

  const [isVoting, setIsVoting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [listUser, setListUser] = useState<ListUser>();
  const [showHidden, setShowHidden] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  useEffect(() => {
    const lu = list?.listUsers?.find((u) => u.userId === user.id);
    setListUser(lu);
  }, [currentUser, list, lists, user.id]);

  useEffect(() => {
    if (router.query.showHidden) {
      setShowHidden(true);
    } else {
      setShowHidden(false);
    }
  }, [router.query]);

  const sourceBy: { [key: string]: string } = {
    owner: '',
    team: 'by a team member',
    application: 'by the applicant',
  };

  const vote = async (l: List, voterId: number) => {
    if (!listUser) return;

    setIsVoting(true);
    try {
      const listVotes = l.listUsers
        .find((u) => u.id === listUser.id)?.listVotes || [];
      const idx = listVotes.findIndex((v) => v.voterId === voterId);
      if (idx > -1) {
        await API.removeVote(l.id, listVotes[idx].id);
        listVotes.splice(idx, 1);
      } else {
        const v = await API.addVote(l.id, listUser.userId);
        listVotes.push(v);
      }

      setLists([
        ...(lists?.filter((s) => s.id !== l.id) || []),
        l,
      ]);
    } catch (err) {
      enqueueSnackbar(`An error occurred.  Please try again later. ${err}`, {
        variant: 'error',
      });
    } finally {
      setIsVoting(false);
    }
  };

  const changeStatus = async (listId: number, listUserId: number, stageId: number) => {
    if (!stageId) return;
    setIsUpdating(true);
    try {
      const l = await API.updateListUserStage(listId, listUserId, stageId);
      setLists([
        ...(lists?.filter((s) => s.id !== l.id) || []),
        l,
      ]);
    } catch (err) {
      enqueueSnackbar(`An error occurred.  Please try again later. ${err}`, {
        variant: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getVoteButton = (l: List) => {
    if (!l.teamId || !currentUser || !listUser) return null;

    const voted = listUser.listVotes.find((v) => v.voterId === currentUser.id);

    return (
      <button
        type="button"
        className="text-alpineTeal hover:text-mediumgray dark:text-hintgray flex gap-2 rounded-2xl px-2 py-1 border border-lightgray font-bold"
        disabled={isVoting}
        onClick={() => vote(l, currentUser.id)}
      >
        {voted ? <ThumbUp fontSize="small" />
          : <ThumbUpOffAlt fontSize="small" />}
        <div>{listUser.listVotes.length}</div>
      </button>
    );
  };

  const getVoters = (votes: ListVote[]) => {
    if (votes.length === 0) return null;

    return (
      <div className="flex gap-1">
        {votes.map((v) => (
          <MobileTooltip title={v.voter.name} key={v.id} arrow>
            <Avatar
              src={imageAvatarUrl(v.voter.avatarUrl, 80)}
              sx={{ width: 30, height: 30 }}
              alt={v.voter.name}
            />
          </MobileTooltip>
        ))}
      </div>
    );
  };

  const showApplication = (appId?: number) => {
    if (setApplicationId) setApplicationId(appId);
  };

  if (listUser && listUser.status === 'hidden' && !showHidden) return null;
  if (!!filteredStages.length && !filteredStages.includes(listUser?.creatorStageId || 0)) {
    return null;
  }

  return (
    <>
      {children}
      {(listUser && list && list.teamId && list.isPrivate) && (
        <div className="px-4 py-2 text-sm flex flex-wrap items-center gap-4">
          <div>
            Saved to this list
            {' '}
            {sourceBy[listUser.source || '']}
            {' '}
            on
            {' '}
            {formatLocalString(listUser.createdAt)}
          </div>
          {getVoteButton(list)}
          {listUser.listVotes.length > 0 && 'Upvoted By: '}
          {getVoters(listUser.listVotes)}
          <button type="button" onClick={() => setShowCommentModal(true)}>
            <Tooltip
              title={listUser.comments?.length ? `View ${listUser.comments.length} comments` : 'Add a comment'}
              arrow
              placement="top"
            >
              {listUser.comments?.length ? (
                <StyledBadge
                  badgeContent={listUser.comments.length}
                  color="secondary"
                >
                  <Chat />
                </StyledBadge>
              ) : (
                <ChatBubble />
              )}
            </Tooltip>
          </button>
          {listUser.source === 'application' && !!user?.applicationId && (
            <button
              type="button"
              className="btn-secondary-xs"
              onClick={() => showApplication(user.applicationId)}
            >
              View application
            </button>
          )}
          {listUser.status === 'new' && (
            <span className="cursor-default text-xs font-semibold inline-block py-1 px-2 ml-2 rounded-full bg-teal uppercase">
              NEW
            </span>
          )}
          {(list.jobId && !!creatorStages?.length) && (
            <div className="flex items-center md:w-1/4 w-full">
              <div className="flex-1 capitalize px-2 py-1">
                <FormControl fullWidth size="small">
                  <InputLabel>Stage</InputLabel>
                  <Select
                    value={listUser.creatorStageId}
                    onChange={(e) => changeStatus(
                      list.id, listUser.id, e.target.value as number,
                    )}
                    label="Stage"
                    disabled={isUpdating}
                  >
                    {creatorStages?.map((stage) => (
                      <MenuItem key={stage.id} value={stage.id}>{stage.name}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem key="edit">
                      <a href={`/teams/${list.teamId}/stages`} target="_blank" rel="noreferrer">
                        Edit Stages
                        <OpenInNew fontSize="small" className="ml-1" />
                      </a>
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
          {showCommentModal && listUser && list && (
            <Modal
              open
              onCancel={() => setShowCommentModal(false)}
            >
              <ListCommentModal
                listUser={listUser}
                listId={list.id}
                onCommentsUpdate={(updatedComments: Comment[]) => {
                  // Update the local listUser state
                  const updatedListUser = {
                    ...listUser,
                    comments: updatedComments,
                  };
                  setListUser(updatedListUser);

                  // Update the lists context
                  if (lists && list) {
                    const updatedList = {
                      ...list,
                      listUsers: list.listUsers?.map((lu) => (
                        lu.id === listUser.id
                          ? { ...lu, comments: updatedComments }
                          : lu
                      )) || [],
                    };

                    setLists([
                      ...(lists.filter((s) => s.id !== list.id) || []),
                      updatedList,
                    ]);
                  }
                }}
              />
            </Modal>
          )}
        </div>
      )}
    </>
  );
};
